/**
 * AdminLogin Page
 * 
 * Login page specifically for admin panel access
 * Separate from regular user login to provide better security
 * and admin-specific authentication flow
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/styles/AdminLogin.css';

// Use environment variable for API URL, fallback to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  /**
   * Handle admin login form submission
   * Authenticates with backend and stores JWT token
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store admin token and user info
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        
        // Redirect to admin dashboard
        navigate('/admin/dashboard');
      } else {
        // Show error message
        setError(data.message || data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        {/* Admin Panel Header */}
        <div className="admin-login-header">
          <h1>Admin Panel</h1>
          <p>Sign in to access administrative functions</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="admin-login-form">
          {/* Error Message */}
          {error && (
            <div className="admin-error-message">
              {error}
            </div>
          )}

          {/* Username Input */}
          <div className="admin-form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
              disabled={isLoading}
            />
          </div>

          {/* Password Input */}
          <div className="admin-form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            className="admin-login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Security Notice */}
        <div className="admin-security-notice">
          <p>🔒 This is a secure admin area. All login attempts are logged.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

